import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import SearchInput from './SearchInput'
import FilterPill from './FilterPill'
import StatusPill from './StatusPill'
import Pagination from './Pagination'

const STATUS_TONE = { paid: 'success', verified: 'primary', pending: 'warning' }

export default withTranslation()(function WinnersInformationSection({ t, winners = [], onRowClick, onExport }) {
  const [q, setQ] = useState('')
  const [prizeType, setPrizeType] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const prizeTypeOptions = [
    { label: t('payout.winners_information.all_prize_types'), value: '' },
    ...Object.entries(t('payout.top_prize_type.types', { returnObjects: true })).map(([value, label]) => ({ label, value }))
  ]
  const statusOptions = [
    { label: t('payout.winners_information.all_statuses'), value: '' },
    { label: t('payout.winners_by_status.paid'), value: 'paid' },
    { label: t('payout.winners_by_status.verified'), value: 'verified' },
    { label: t('payout.winners_by_status.pending'), value: 'pending' }
  ]

  const filtered = winners.filter((winner) => {
    const query = q.trim().toLowerCase()
    if (query && !`${winner.name} ${winner.phone}`.toLowerCase().includes(query)) return false
    if (prizeType && winner.prizeTypeKey !== prizeType) return false
    if (status && winner.status !== status) return false
    return true
  })

  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize)

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={q}
          onChange={(value) => { setQ(value); setPage(0) }}
          placeholder={t('payout.winners_information.search_placeholder')}
        />

        <FilterPill
          label={t('payout.winners_information.all_prize_types')}
          active={prizeType ? { label: prizeTypeOptions.find((option) => option.value === prizeType)?.label } : null}
          options={prizeTypeOptions}
          onSelect={(option) => { setPrizeType(option.value); setPage(0) }}
        />

        <FilterPill
          label={t('payout.winners_information.all_statuses')}
          active={status ? { label: statusOptions.find((option) => option.value === status)?.label } : null}
          options={statusOptions}
          onSelect={(option) => { setStatus(option.value); setPage(0) }}
        />

        <button
          type="button"
          onClick={onExport}
          className="ml-auto flex items-center gap-1.5 border border-border-lines rounded-full h-10 px-4 text-sm font-medium text-dark-blue whitespace-nowrap"
        >
          <UIcons.UilExport size={16} /> {t('payout.winners_information.export')}
        </button>
      </div>

      <div className="w-full bg-white border border-border-lines rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-secondary bg-background">
                <th className="py-2.5 px-4 font-medium uppercase whitespace-nowrap">{t('payout.winners_information.columns.winner_name')}</th>
                <th className="py-2.5 px-4 font-medium uppercase whitespace-nowrap">{t('payout.winners_information.columns.phone_number')}</th>
                <th className="py-2.5 px-4 font-medium uppercase whitespace-nowrap">{t('payout.winners_information.columns.location')}</th>
                <th className="py-2.5 px-4 font-medium uppercase whitespace-nowrap">{t('payout.winners_information.columns.prize_type')}</th>
                <th className="py-2.5 px-4 font-medium uppercase whitespace-nowrap">{t('payout.winners_information.columns.prize_name')}</th>
                <th className="py-2.5 px-4 font-medium uppercase whitespace-nowrap">{t('payout.winners_information.columns.payout_reference')}</th>
                <th className="py-2.5 px-4 font-medium uppercase whitespace-nowrap">{t('payout.winners_information.columns.status')}</th>
                <th className="py-2.5 px-4" />
              </tr>
            </thead>

            <tbody>
              {
                paged.length ? paged.map((winner) => (
                  <tr
                    key={winner.id}
                    className="border-t border-border-lines cursor-pointer hover:bg-background"
                    onClick={() => onRowClick(winner)}
                  >
                    <td className="py-4 px-4 text-dark-blue whitespace-nowrap">{winner.name}</td>
                    <td className="py-4 px-4 text-dark-blue whitespace-nowrap">{winner.phone}</td>
                    <td className="py-4 px-4 text-dark-blue whitespace-nowrap">{winner.location}</td>
                    <td className="py-4 px-4 text-dark-blue whitespace-nowrap">{winner.prizeType}</td>
                    <td className="py-4 px-4 text-dark-blue whitespace-nowrap">{winner.prizeName}</td>
                    <td className="py-4 px-4 text-dark-blue whitespace-nowrap">{winner.payoutReference}</td>
                    <td className="py-4 px-4"><StatusPill text={winner.statusLabel ?? winner.status} tone={STATUS_TONE[winner.status] ?? 'success'} /></td>
                    <td className="py-4 px-4 text-secondary"><UIcons.UilAngleRight size={16} /></td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <p className="font-semibold text-dark-blue">{t('payout.winners_information.empty_title')}</p>
                      <p className="text-sm text-secondary mt-1">{t('payout.winners_information.empty_hint')}</p>
                    </td>
                  </tr>
                )
              }
            </tbody>
          </table>
        </div>

        {
          filtered.length > 0 && (
            <Pagination
              tKey="payout.winners_information.table"
              total={filtered.length}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(0) }}
            />
          )
        }
      </div>
    </div>
  )
})
